import { getCurrentInstance } from "vue";
import {
  createComment,
  deleteComment,
  getComment,
  markCommentsSeen,
  reopenComment,
  resolveComment,
  updateComment,
} from "@/api";
import { generateCommentId } from "./commentUtils";

const currentAccountId = () => JSON.parse(localStorage.getItem("currentAccountID"));

/**
 * Comment mutations for the combined Chatter drawer (ER-12315). Every call
 * resolves to a result object and reports failures through the notification
 * bar, so callers only decide what the UI does next.
 */
export function useCommentActions() {
  const proxy = getCurrentInstance()?.proxy;
  // One in-flight draft per composer: a retry of the same text reuses its id.
  const pendingCreates = new Map();

  const fail = (message, error) => {
    console.error(message, error);
    proxy?.$notifications?.error(message);
  };

  const create = async ({ objectType, objectId, body, parentId = null }) => {
    const key = parentId ? `reply:${parentId}` : "root";
    const pending = pendingCreates.get(key);
    const id = pending && pending.body === body ? pending.id : generateCommentId();
    pendingCreates.set(key, { id, body });
    try {
      const payload = { id, object_type: objectType, object_id: objectId, body };
      if (parentId) payload.parent_id = parentId;
      const { data } = await createComment(currentAccountId(), payload);
      pendingCreates.delete(key);
      return { ok: true, comment: data };
    } catch (error) {
      fail("Failed to post comment.", error);
      return { ok: false };
    }
  };

  const save = async (comment, body) => {
    try {
      const { data } = await updateComment(currentAccountId(), comment.id, {
        body,
        version: comment.version,
      });
      return { ok: true, comment: data };
    } catch (error) {
      if (error?.response?.status === 409) return { ok: false, conflict: true };
      fail("Failed to save comment.", error);
      return { ok: false };
    }
  };

  const reload = async (commentId) => {
    try {
      const { data } = await getComment(currentAccountId(), commentId);
      return { ok: true, comment: data };
    } catch (error) {
      fail("Failed to reload comment.", error);
      return { ok: false };
    }
  };

  const remove = async (commentId) => {
    try {
      await deleteComment(currentAccountId(), commentId);
      return { ok: true };
    } catch (error) {
      fail("Failed to delete comment.", error);
      return { ok: false };
    }
  };

  const setResolved = async (commentId, resolved) => {
    try {
      const call = resolved ? resolveComment : reopenComment;
      const { data } = await call(currentAccountId(), commentId);
      return { ok: true, comment: data };
    } catch (error) {
      fail(resolved ? "Failed to resolve thread." : "Failed to reopen thread.", error);
      return { ok: false };
    }
  };

  // Receipts are best-effort: a failure is logged but never interrupts reading.
  const markSeen = async (commentIds) => {
    try {
      await markCommentsSeen(currentAccountId(), commentIds);
      return true;
    } catch (error) {
      console.error("Failed to record seen comments", error);
      return false;
    }
  };

  return { create, save, reload, remove, setResolved, markSeen };
}
